import { useState } from 'react';
import { type ClassNames, DayPicker } from 'react-day-picker';

import type { Locale } from '@/utils/localeFormatters';

import { type DayBounds, isDayBlocked, PICKER_LOCALES } from './pickerDays';

import styles from './PickerCalendar.module.scss';

// The calendar is styled here with theme tokens; react-day-picker's own stylesheet is not
// loaded. Parts left out keep the library's unstyled class names.
const CLASS_NAMES: Partial<ClassNames> = {
    root: styles.root,
    months: styles.months,
    month_caption: styles.caption,
    caption_label: styles.captionLabel,
    nav: styles.nav,
    button_previous: styles.navButton,
    button_next: styles.navButton,
    chevron: styles.chevron,
    month_grid: styles.grid,
    weekday: styles.weekday,
    day: styles.day,
    day_button: styles.dayButton,
    selected: styles.selected,
    hidden: styles.hidden,
};

interface PickerCalendarProps {
    id: string;
    label: string;
    date: Date | null;
    locale: Locale;
    bounds: DayBounds;
    /** Moves focus to the selected day (or today) when the calendar opens. */
    autoFocus: boolean;
    onSelect: (day: Date) => void;
}

/** The month calendar under a date input: one month, outside days hidden. */
export function PickerCalendar({
    id,
    label,
    date,
    locale,
    bounds,
    autoFocus,
    onSelect,
}: PickerCalendarProps) {
    // Opens on the picked day's month, or this month without one.
    const [month, setMonth] = useState<Date>(() => date ?? new Date());

    return (
        <div id={id} role="dialog" aria-label={label} className={styles.popover}>
            <DayPicker
                mode="single"
                required
                selected={date ?? undefined}
                onSelect={onSelect}
                month={month}
                onMonthChange={setMonth}
                disabled={(day: Date) => isDayBlocked(day, bounds)}
                locale={PICKER_LOCALES[locale]}
                weekStartsOn={locale === 'en' ? 0 : 1}
                autoFocus={autoFocus}
                classNames={CLASS_NAMES}
            />
        </div>
    );
}
